/**
 * AP-36 Phase C — zwei Ergänzungen für Einträge, die kein einzelnes Feld sind:
 *
 * - `useEntwurf` — ein **Eintrag als Gruppe** mit lokalem Entwurf: eine
 *   Scan-Regel mit Segmenten, ein Feld eines Ref-Schemas, eine Zeile mit
 *   dynamischen Werten. Die Maske bearbeitet den Entwurf wie bisher mit
 *   `setze`; gespeichert wird beim Verlassen des Eintrags (Hülle), und zwar als
 *   **Operation** auf dem aktuellen Stand (8.3 a). Esc verwirft den Entwurf.
 * - `useAenderung` — eine sofortige Änderung ohne Eingabefeld (Häkchen in
 *   einer Liste, Reihenfolge, Entfernen) mit Zustand für `FeldStatus`.
 */
import { useCallback, useEffect, useId, useRef, useState, type FocusEvent, type KeyboardEvent } from 'react'
import type { Status, Zustand } from './hooks'
import type { Datensatz, Ergebnis, Speicherstelle } from './stelle'
import { gleich } from './vergleich'

function useZustandMitHaken() {
  const [zustand, setRoh] = useState<Zustand>('ruhig')
  const zeit = useRef<ReturnType<typeof setTimeout>>()
  const setZustand = useCallback((z: Zustand) => {
    clearTimeout(zeit.current)
    setRoh(z)
    if (z === 'gespeichert') zeit.current = setTimeout(() => setRoh('ruhig'), 1500)
  }, [])
  useEffect(() => () => clearTimeout(zeit.current), [])
  return [zustand, setZustand] as const
}

export interface EntwurfOptionen<T> {
  /** Eindeutig je Eintrag, z. B. `scanRules:r1`. */
  kennung: string
  /** Der gespeicherte Eintrag (aus der Basis der Stelle abgeleitet); `null`, wenn es ihn nicht mehr gibt. */
  gespeichert: T | null
  /** Die Spalten, die die Operation schreibt (für Anzeige und Zählung). */
  felder: string[]
  /** Macht aus dem Entwurf die Operation auf dem aktuellen Stand. */
  operation: (entwurf: T) => (aktuell: Datensatz) => Datensatz
  /** Grund, warum der Entwurf nicht gesendet wird — oder `null`. */
  pruefe?: (entwurf: T) => string | null
  label?: string
  gesperrt?: boolean
}

export interface Entwurf<T> extends Status {
  entwurf: T | null
  setze: (neu: T | ((alt: T) => T)) => void
  geaendert: boolean
  /** An die Hülle des Eintrags (Karte, Zeile). */
  huelle: {
    onFocus: () => void
    onBlur: (e: FocusEvent<HTMLElement>) => void
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => void
  }
  verwerfen: () => void
  /** Sofort speichern (z. B. nach einer Auswahl, die keinen Fokuswechsel auslöst). */
  speichere: () => Promise<Ergebnis | null>
}

export function useEntwurf<T>(stelle: Speicherstelle<unknown>, opts: EntwurfOptionen<T>): Entwurf<T> {
  const [entwurf, setEntwurf] = useState<T | null>(opts.gespeichert)
  const [geaendert, setGeaendert] = useState(false)
  const [zustand, setZustand] = useZustandMitHaken()
  const [fehler, setFehler] = useState<string | null>(null)
  const optsRef = useRef(opts)
  optsRef.current = opts
  const entwurfRef = useRef(entwurf)
  entwurfRef.current = entwurf
  const inArbeit = useRef<Promise<Ergebnis | null> | null>(null)

  // Unverändert folgt der Entwurf dem gespeicherten Stand (fremde Änderungen, 7.6).
  useEffect(() => {
    if (!geaendert && !gleich(opts.gespeichert, entwurfRef.current)) setEntwurf(opts.gespeichert)
  }, [opts.gespeichert, geaendert])

  // Beim Schließen des Tabs warnen, solange ein Entwurf offen ist (S-21).
  useEffect(() => {
    if (geaendert) stelle.ungesendet.set(opts.kennung, { patch: () => null, keepalive: false })
    else stelle.ungesendet.delete(opts.kennung)
    return () => {
      stelle.ungesendet.delete(opts.kennung)
      stelle.pruefFehler.delete(opts.kennung)
    }
  }, [stelle, opts.kennung, geaendert])

  const speichereEinmal = useCallback(async (): Promise<Ergebnis | null> => {
    const o = optsRef.current
    const e = entwurfRef.current
    if (!geaendertRef.current || e === null || o.gesperrt) return null
    const grund = o.pruefe?.(e) ?? null
    if (grund) {
      stelle.pruefFehler.add(o.kennung)
      setFehler(grund)
      setZustand('fehler')
      return null
    }
    stelle.pruefFehler.delete(o.kennung)
    setFehler(null)
    setZustand('speichert')
    const gesendet = e
    const ergebnis = await stelle.sende({
      kennung: o.kennung,
      felder: o.felder,
      labels: o.label ? Object.fromEntries(o.felder.map((f) => [f, o.label!])) : undefined,
      operation: o.operation(gesendet),
    })
    if (ergebnis.art === 'gespeichert') {
      // Hat der Benutzer weiter bearbeitet, bleibt der neue Entwurf offen.
      if (entwurfRef.current === gesendet) setGeaendert(false)
      setZustand('gespeichert')
    } else if (ergebnis.art === 'fehler') {
      setFehler(ergebnis.text)
      setZustand('fehler')
    } else if (ergebnis.art === 'verworfen') {
      setGeaendert(false)
      setEntwurf(optsRef.current.gespeichert)
      setZustand('ruhig')
    }
    return ergebnis
  }, [stelle, setZustand])

  const geaendertRef = useRef(geaendert)
  geaendertRef.current = geaendert

  const speichere = useCallback((): Promise<Ergebnis | null> => {
    if (inArbeit.current) return inArbeit.current
    const p = speichereEinmal().finally(() => {
      inArbeit.current = null
    })
    inArbeit.current = p
    return p
  }, [speichereEinmal])

  const verwerfen = useCallback(() => {
    setEntwurf(optsRef.current.gespeichert)
    setGeaendert(false)
    setFehler(null)
    setZustand('ruhig')
    stelle.pruefFehler.delete(optsRef.current.kennung)
  }, [stelle, setZustand])

  return {
    entwurf,
    geaendert,
    zustand,
    fehler,
    erneut: zustand === 'fehler' && stelle.serverFehler.has(opts.kennung) ? () => void speichere() : undefined,
    setze: (neu) => {
      setEntwurf((alt) => (typeof neu === 'function' ? (neu as (a: T) => T)(alt as T) : neu))
      setGeaendert(true)
      geaendertRef.current = true
      if (fehler && zustand === 'fehler') setFehler(null)
    },
    huelle: {
      onFocus: () => undefined,
      onBlur: (e) => {
        if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return
        void speichere()
      },
      onKeyDown: (e) => {
        if (e.key === 'Escape' && geaendertRef.current) {
          e.preventDefault()
          e.stopPropagation()
          verwerfen()
        }
      },
    },
    verwerfen,
    speichere,
  }
}

/**
 * Eine sofortige Änderung (Operation oder Patch) mit Zustand.
 *
 * Jeder Aufruf bekommt eine **eigene** Kennung, außer `wert` ist gesetzt: Zwei
 * Operationen hintereinander („Wert hinzu", „Wert weg") dürfen einander nicht
 * ersetzen. Mit `wert` (etwa `aggregation:km`) ersetzt der neuere Wert den
 * wartenden — wie bei einem Feld (7.2).
 */
export function useAenderung(stelle: Speicherstelle<unknown>, kennungBasis?: string) {
  const id = useId()
  const basis = kennungBasis ?? id
  const zaehler = useRef(0)
  const [zustand, setZustand] = useZustandMitHaken()
  const [fehler, setFehler] = useState<string | null>(null)
  const letzte = useRef<Parameters<Speicherstelle<unknown>['sende']>[0] | null>(null)

  const sende = useCallback(
    async (auftrag: { felder: string[]; patch?: Datensatz; operation?: (aktuell: Datensatz) => Datensatz; labels?: Record<string, string>; wert?: string }) => {
      const { wert, ...rest } = auftrag
      const voll = { kennung: wert ? `${basis}:${wert}` : `${basis}:${++zaehler.current}`, ...rest }
      letzte.current = voll
      setFehler(null)
      setZustand('speichert')
      const ergebnis = await stelle.sende(voll)
      if (ergebnis.art === 'gespeichert') setZustand('gespeichert')
      else if (ergebnis.art === 'fehler') {
        setFehler(ergebnis.text)
        setZustand('fehler')
      } else if (ergebnis.art === 'verworfen') setZustand('ruhig')
      return ergebnis
    },
    [stelle, basis, setZustand],
  )

  const status: Status = {
    zustand,
    fehler,
    erneut: zustand === 'fehler' && letzte.current ? () => void sende(letzte.current!) : undefined,
  }
  return { sende, status }
}
